
import HtmlRich from '@gid/vue-common/components/HtmlRich.vue';
import { LiveAgent } from '@gid/vue-common/components/ticketing/interface/ticketing';
import { formatContentForIframe } from '@gid/vue-common/utils';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { mapGetters } from 'vuex';
import LiveAgentAttachment from './LiveAgentAttachment.vue';

@Component({
  components: {
    LiveAgentAttachment,
    HtmlRich,
  },
  computed: {
    ...mapGetters(['access_token']),
  },
})
export default class LiveAgentMessage extends Vue {
  @Prop({ default: null }) message!: LiveAgent.Message;
  // The LiveAgent ticket code (e.g. FNS-FHQPH-563) — a string, not a number.
  @Prop({ default: null }) ticketId!: string;
  @Prop({ default: false }) loadHeight!: boolean;

  fromName: string = '';
  fromEmail: string = '';

  created() {
    this.fromDetails();
  }
  get formattedContent() {
    return formatContentForIframe(this.message?.content);
  }

  get isDarkMode(): boolean {
    return (this as any).$accessibility?.isDarkMode ?? false;
  }

  onIframeLoad() {
    this.syncIframeDarkMode();
    this.adjustIframeHeight();
  }

  /**
   * Dark mode inverts the whole email document via a CSS filter on the
   * iframe (see <style> below) so authored-for-white mail becomes
   * light-on-dark. That would also invert embedded images, so a style
   * injected into the document re-inverts media back to its original
   * colors, gated on an html class this method keeps in sync with the
   * app theme.
   */
  syncIframeDarkMode() {
    const iframe = this.$refs.iframeRef as HTMLIFrameElement;
    const doc = iframe && iframe.contentWindow && iframe.contentWindow.document;
    if (!doc || !doc.documentElement) return;
    if (!doc.getElementById('gid-dark-inversion')) {
      const style = doc.createElement('style');
      style.id = 'gid-dark-inversion';
      style.textContent =
        'html.gid-inverted img, html.gid-inverted svg, html.gid-inverted video' +
        ' { filter: invert(1) hue-rotate(180deg); }';
      (doc.head || doc.documentElement).appendChild(style);
    }
    doc.documentElement.classList.toggle('gid-inverted', this.isDarkMode);
  }

  adjustIframeHeight() {
    this.$nextTick(() => {
      const iframe = this.$refs.iframeRef as HTMLIFrameElement;
      if (iframe && iframe.contentWindow && iframe.contentWindow.document) {
        // Re-measure on every call (iframe `load`, parent `loadHeight`
        // toggle): a srcdoc iframe lays out asynchronously, so an early
        // measurement undershoots — caching it would freeze the frame at
        // that height. Collapse first so scrollHeight reports the content,
        // not the current frame viewport; both style writes happen in the
        // same task, so nothing paints in between.
        iframe.style.height = '0px';
        const contentHeight =
          iframe.contentWindow.document.documentElement.scrollHeight;
        iframe.style.height = contentHeight + 10 + 'px';
      }
    });
  }
  extractNameAndEmail(str) {
    const regex = /^(.*?)\s*<(.+?)>$/;
    const match = str.match(regex);

    if (match) {
      return { name: match[1].trim(), email: match[2].trim() };
    }

    return null;
  }
  fromDetails() {
    if (this.message?.from) {
      const result = this.extractNameAndEmail(this.message.from);
      this.fromEmail = result?.email;
      this.fromName = result?.name;
    }
  }
  @Watch('loadHeight')
  onLoadHeight(v: boolean) {
    if (v) this.adjustIframeHeight();
  }

  @Watch('isDarkMode')
  onDarkModeChange() {
    this.syncIframeDarkMode();
  }
}
